// environment.js — the zen garden the bonsai sits in.
//
// Builds and adds all the static scene dressing: the raked-sand ground
// (procedural texture + bump map), the garden border, the pot (soil, legs,
// rocks), the standing stones, and the decorative GLB pebble. createEnvironment()
// is the entry point, called once from main.js init() after the scene exists.
//
// Everything here adds to app.scene. The noise2D/perlinNoise helpers and the
// createTaperedBoxGeometry builder are used only by this module, so they live
// here rather than in core.

import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { app, Colors } from './core.js';
import { injectWindSway } from './foliageShader.js';
import { setDeerActive } from './deer.js';
import { setShibaActive } from './shiba.js';

const loader = new GLTFLoader();
let pot = null; // current pot group; replaced when the pot is rebuilt
let lantern = null; // GLB stone lantern; lazily loaded on first enable, then cached
let torii = null; // GLB torii gate; lazily loaded on first enable, then cached
let shortLantern = null; // GLB short stone lantern; lazily loaded, then cached
let waterCan = null; // GLB watering can; lazily loaded on first enable, then cached
let stool = null; // GLB stool; lazily loaded on first enable, then cached
let tea = null; // GLB tea set resting on the stool; lazily loaded, then cached
let bamboo = null; // GLB bamboo + moss patch in the garden corner; lazily loaded, then cached
let house = null; // GLB house backdrop sitting just beyond the garden; lazily loaded, then cached

// The stool's seat in the garden, shared by setStoolVisible and setTeaVisible so
// the tea set always lands on the stool. STOOL_TOP_Y is the sand level (-29) plus
// the scaled height, since the stool is seated base-on-sand at exactly that height.
const STOOL_POS = { x: -24, z: 58 }; // close to the bonsai, front-left
const STOOL_HEIGHT = 26; // world units; a low accent on the sand
const STOOL_TOP_Y = -29 + STOOL_HEIGHT;
let gravel = null; // zen-garden gravel plane; tinted by the Darker Gravel toggle
let gravelDark = false; // settings toggle: tint the gravel a darker gray
let zenRocksGroup = null; // the standing garden rocks; frosted in winter mode
let sceneSnow = null; // lazily-built group of snow caps on the pot + rocks
let _snowOn = false; // current winter frost state (idempotent toggle guard)
let _autumnOn = false; // current autumn-grass state (idempotent toggle guard)
let groundShader = null; // distant grass disc's compiled shader, for the winter/autumn uniforms

// ── Rock surface normals ──────────────────────────────────────────────────────
// Shares the settings drawer's "Surface Textures" checkbox with the bark normals
// (bark.js owns the bark side; settings.js drives both from the one toggle). Rocks
// are built once in createEnvironment, so each rock material registers itself and
// which normal it wants; setRockTexture applies or clears them all. Loaded lazily
// and cached, in linear space (NoColorSpace) like the bark normals.
const _rockNormalLoader = new THREE.TextureLoader();
const _rockNormals = {};
let _rockTextureEnabled = false;
const _rockMaterials = []; // { material, file } registered at build time

function getRockNormal(file) {
  if (_rockNormals[file]) return _rockNormals[file];
  const tex = _rockNormalLoader.load(`assets/textures/${file}`);
  tex.colorSpace = THREE.NoColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  _rockNormals[file] = tex;
  return tex;
}

// Register a rock material + its normal file, applying it now if textures are on.
function registerRock(material, file) {
  _rockMaterials.push({ material, file });
  if (_rockTextureEnabled) {
    material.normalMap = getRockNormal(file);
    material.needsUpdate = true;
  }
}

// Settings-drawer toggle (paired with bark): apply or clear the rock normals across
// every registered rock material. Only hits the loader when enabling, so the rock
// normal jpgs aren't fetched until the texture toggle is first switched on.
export function setRockTexture(enabled) {
  _rockTextureEnabled = !!enabled;
  for (const { material, file } of _rockMaterials) {
    material.normalMap = enabled ? getRockNormal(file) : null;
    material.needsUpdate = true;
  }
}

// Builds the distant mountain-ring backdrop and returns the mesh (does not add
// it to the scene). Toggled on demand via setMountainsVisible().
function buildMountainRing() {
  const radius = 1826;
  const baseHeight = 180;
  const segments = 148;

  const mountainGeo = new THREE.CylinderGeometry(
    radius, radius, baseHeight, segments, 16, true
  );

  const pos = mountainGeo.attributes.position;
  const vertex = new THREE.Vector3();
  const displacementScale = 250;
  const radialScale = 120;

  // === Shape mountains ===
  for (let i = 0; i < pos.count; i++) {
    vertex.fromBufferAttribute(pos, i);

    if (vertex.y > 0) {
      const angle = Math.atan2(vertex.z, vertex.x);
      const noise = (Math.sin(angle * 5) * 0.5 + 0.5) * Math.random();

      vertex.y += noise * displacementScale;

      const dir = new THREE.Vector2(vertex.x, vertex.z).normalize();
      vertex.x += dir.x * noise * radialScale;
      vertex.z += dir.y * noise * radialScale;

      pos.setXYZ(i, vertex.x, vertex.y, vertex.z);
    }
  }
  mountainGeo.computeVertexNormals();

  // === Colors (simple vertical gradient) ===
  const colors = [];
  const snowAmt = []; // per-vertex snow coverage, ramped in on the upper slopes for winter mode
  const color1 = new THREE.Color(0x36454F); // base
  const color2 = new THREE.Color(0x708090); // mid
  const color3 = new THREE.Color(0xC0C0C0); // peaks

  // Find min & max height after displacement
  let minY = Infinity, maxY = -Infinity;
  for (let i = 0; i < pos.count; i++) {
    vertex.fromBufferAttribute(pos, i);
    if (vertex.y < minY) minY = vertex.y;
    if (vertex.y > maxY) maxY = vertex.y;
  }

  for (let i = 0; i < pos.count; i++) {
    vertex.fromBufferAttribute(pos, i);
    const heightRatio = (vertex.y - minY) / (maxY - minY);

    let mountainColor = new THREE.Color();
    if (heightRatio < 0.65) {
      mountainColor.lerpColors(color1, color2, heightRatio / 0.65);
    } else {
      mountainColor.lerpColors(color2, color3, (heightRatio - 0.65) / 0.35);
    }

    colors.push(mountainColor.r, mountainColor.g, mountainColor.b);
    snowAmt.push(THREE.MathUtils.smoothstep(heightRatio, 0.78, 0.94)); // high snowline → just the peaks cap
  }
  mountainGeo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  mountainGeo.setAttribute('aSnow', new THREE.Float32BufferAttribute(snowAmt, 1));

  // === Material ===
  const mountainMat = new THREE.MeshStandardMaterial({
    vertexColors: true,
    flatShading: true,
    side: THREE.BackSide
  });
  // Winter: blend the upper slopes/peaks toward snow-white. uWinter is flipped by
  // setEnvironmentSnow(); the per-vertex aSnow ramp keeps the lower slopes bare.
  mountainMat.onBeforeCompile = (shader) => {
    shader.uniforms.uWinter = { value: _snowOn ? 1 : 0 };
    mountainMat.userData.shader = shader;
    shader.vertexShader = shader.vertexShader
      .replace('void main() {', 'attribute float aSnow;\nvarying float vSnow;\nvoid main() {')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n  vSnow = aSnow;');
    shader.fragmentShader = shader.fragmentShader
      .replace('void main() {', 'uniform float uWinter;\nvarying float vSnow;\nvoid main() {')
      .replace(
        '#include <color_fragment>',
        '#include <color_fragment>\n  float snowCap = clamp(uWinter * vSnow, 0.0, 1.0);\n  diffuseColor.rgb = mix(diffuseColor.rgb, vec3(1.0), snowCap);'
      )
      // Self-lit lift on the caps so the distant fog doesn't wash the white away.
      .replace(
        '#include <emissivemap_fragment>',
        '#include <emissivemap_fragment>\n  totalEmissiveRadiance += vec3(0.55, 0.58, 0.62) * snowCap;'
      );
  };

  const mountainRing = new THREE.Mesh(mountainGeo, mountainMat);
  mountainRing.position.y = -38;
  return mountainRing;
}

// Builds the lone Mt-Fuji cone — a single snow-capped peak set further out than
// the mountain ring so it rises behind the range. Returns the mesh (not added to
// the scene); toggled via setFujiVisible(). Like the ring it carries an aSnow
// ramp + uWinter shader so winter blankets it lower, but its upper cap is baked
// in so the signature snow cap shows in every season.
function buildFujiMountain() {
  // The cone runs far taller/wider than what reads above the horizon: only the
  // top ~760 units are the "visible" peak (see VIS_SPAN below); the rest of the
  // skirt plunges deep below the horizon so its base never shows as a hard edge —
  // it just dissolves into the distance/fog. Visible slope ≈ the old 760→96 cone.
  const topRadius = 94;   // flattened summit (the crater plateau), not a sharp point
  const height = 1400;
  const radial = 140;     // dense enough for the fine snowline fringe to resolve
  const rings = 52;

  // --- Fuji silhouette: radius as a function of distance *below the summit* ---
  // Two slopes joined by a smooth bend give the signature concave sweep — a gentle
  // upper cone, then a shallower, wider-flaring skirt. Keyed to depth-below-summit
  // (not the full cone height) so the curve sits in the visible peak, not the
  // buried lower geometry. The skirt keeps widening down to baseRadius in the fog.
  const summitY = height / 2;
  const KNEE = 300;         // depth below summit where the curve sits (~mid visible peak)
  const R_MID = 480;        // radius at the knee
  const UPPER_SLOPE = (R_MID - topRadius) / KNEE; // gentle upper cone (≈38° from horizontal)
  const LOWER_SLOPE = 1.7;  // shallower angle → wider flared base (concave skirt)
  const baseRadius = R_MID + LOWER_SLOPE * (height - KNEE); // ≈ 2350, far below horizon

  // Capped truncated cone: a small top radius gives Fuji's signature flat summit.
  const fujiGeo = new THREE.CylinderGeometry(topRadius, baseRadius, height, radial, rings, false);
  const pos = fujiGeo.attributes.position;
  const vertex = new THREE.Vector3();

  // Reshape the straight frustum to the two-slope profile above; a touch of
  // low-freq noise (faded out near the clean summit) softens the silhouette.
  for (let i = 0; i < pos.count; i++) {
    vertex.fromBufferAttribute(pos, i);
    const d = summitY - vertex.y; // 0 at summit → height at base
    const r = Math.hypot(vertex.x, vertex.z);
    if (r > 1e-3) { // skip the flat cap centers so the plateau stays flat
      const angle = Math.atan2(vertex.z, vertex.x);
      const rUpper = topRadius + UPPER_SLOPE * d;
      const rLower = R_MID + LOWER_SLOPE * (d - KNEE);
      const bend = THREE.MathUtils.smoothstep(d, KNEE - 170, KNEE + 170); // the gentle curve
      const fade = THREE.MathUtils.clamp(d / 380, 0, 1); // calm the noise near the summit
      const wobble = 1 + (Math.sin(angle * 7) * 0.5 + Math.sin(angle * 3 + 1.7)) * 0.018 * fade;
      const radius = THREE.MathUtils.lerp(rUpper, rLower, bend) * wobble;
      vertex.x = Math.cos(angle) * radius;
      vertex.z = Math.sin(angle) * radius;
      pos.setXYZ(i, vertex.x, vertex.y, vertex.z);
    }
  }
  fujiGeo.computeVertexNormals();

  // === Colors: hazy slate-blue slopes → white summit cap ===
  // Ramps are keyed to distance *below the summit* (VIS_SPAN), not the full cone
  // height, so the cap stays a cap no matter how far the skirt extends down. hv:
  // 1 at the summit → 0 once VIS_SPAN below it (everything deeper is plain slate).
  const colors = [];
  const snowAmt = [];
  const base = new THREE.Color(0x4a5a72);  // skirt (distance-blued slate)
  const mid = new THREE.Color(0x6a7a92);   // upper slope
  const cap = new THREE.Color(0xffffff);   // permanent snow cap
  const VIS_SPAN = 760;  // how far down from the summit the gradient/cap spans
  const SNOWLINE = 0.58; // (in hv) baked cap starts here → top ~165 units

  for (let i = 0; i < pos.count; i++) {
    vertex.fromBufferAttribute(pos, i);
    const hv = THREE.MathUtils.clamp(1 - (summitY - vertex.y) / VIS_SPAN, 0, 1);
    const c = new THREE.Color();
    if (hv < SNOWLINE) {
      c.lerpColors(base, mid, hv / SNOWLINE);
    } else {
      // Snowline edge: layered high frequencies at small amplitude read as a
      // fine, irregular fringe (snow fingers running down gullies) rather than
      // the big regular scallops a single low-freq wave gives (cartoonish).
      const angle = Math.atan2(vertex.z, vertex.x);
      const fringe = Math.sin(angle * 29) * 0.5
                   + Math.sin(angle * 61 + 1.3) * 0.3
                   + Math.sin(angle * 17 + 0.6) * 0.2;
      const edge = SNOWLINE + fringe * 0.014;
      const t = THREE.MathUtils.smoothstep(hv, edge, edge + 0.1);
      c.lerpColors(mid, cap, t);
    }
    colors.push(c.r, c.g, c.b);
    // Winter pushes the snow further down the skirt.
    snowAmt.push(THREE.MathUtils.smoothstep(hv, 0.45, 0.74));
  }
  fujiGeo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  fujiGeo.setAttribute('aSnow', new THREE.Float32BufferAttribute(snowAmt, 1));

  const fujiMat = new THREE.MeshStandardMaterial({
    vertexColors: true,
    flatShading: true,
    side: THREE.DoubleSide,
  });
  // Same winter recipe as the mountain ring: uWinter blends the lower slopes
  // toward snow-white, gated per-vertex by aSnow.
  fujiMat.onBeforeCompile = (shader) => {
    shader.uniforms.uWinter = { value: _snowOn ? 1 : 0 };
    fujiMat.userData.shader = shader;
    shader.vertexShader = shader.vertexShader
      .replace('void main() {', 'attribute float aSnow;\nvarying float vSnow;\nvoid main() {')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n  vSnow = aSnow;');
    shader.fragmentShader = shader.fragmentShader
      .replace('void main() {', 'uniform float uWinter;\nvarying float vSnow;\nvoid main() {')
      .replace(
        '#include <color_fragment>',
        '#include <color_fragment>\n  float snowCap = clamp(uWinter * vSnow, 0.0, 1.0);\n  diffuseColor.rgb = mix(diffuseColor.rgb, vec3(1.0), snowCap);'
      )
      .replace(
        '#include <emissivemap_fragment>',
        '#include <emissivemap_fragment>\n  totalEmissiveRadiance += vec3(0.55, 0.58, 0.62) * snowCap;'
      );
  };

  const fuji = new THREE.Mesh(fujiGeo, fujiMat);
  // Set further out than the ring (radius 1826) so it sits behind the range. The
  // summit lands at +485 world (clearing the ring's ~+300 silhouette) while the
  // long skirt runs down to ≈ -1000, far below the horizon, dissolving into fog.
  const azimuth = -2.1; // radians; tucked behind, off-axis from the default view
  const dist = 2280;
  const peakY = 485;
  fuji.position.set(Math.cos(azimuth) * dist, peakY - height / 2, Math.sin(azimuth) * dist);
  return fuji;
}

///
// ZEN GARDEN
///
function createZenGardenTexture() {
  const textureSize = 1024;
  const canvas = document.createElement('canvas');
  canvas.width = textureSize;
  canvas.height = textureSize;
  const ctx = canvas.getContext('2d');
  
  // Fill with base sand color first
  ctx.fillStyle = '#E6D5C3';
  ctx.fillRect(0, 0, textureSize, textureSize);
  
  // Create image data for procedural generation
  const imageData = ctx.createImageData(textureSize, textureSize);
  const data = imageData.data;
  
  // Define ripple positions (matching your zen garden rocks)
  const rockPositions = [
    { x: 0.2, y: 0.4, radius: 0.12 },  // Increased radius for more visible ripples
    { x: 0.18, y: 0.45, radius: 0.10 },
    { x: 0.35, y: 0.7, radius: 0.11 },
    { x: 0.85, y: 0.8, radius: 0.13 },
    { x: 0.75, y: 0.85, radius: 0.12 },
    // { x: 0.5, y: 0.5, radius: 0.12 }
  ];
  
  // Generate zen garden sand patterns
  for (let x = 0; x < textureSize; x++) {
    for (let y = 0; y < textureSize; y++) {
      const index = (y * textureSize + x) * 4;
      
      // Normalize coordinates to 0-1 range
      const nx = x / textureSize;
      const ny = y / textureSize;
      
      // Base sand color
      let r = 230, g = 213, b = 195; // #E6D5C3 in RGB
      
      // Check if we're near a rock for circular ripples
      let rippleIntensity = 0;
      
      for (const rock of rockPositions) {
        const dx = nx - rock.x;
        const dy = ny - rock.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < rock.radius * 2.5) { // Extended ripple area
          // Create concentric ripples
          const rippleDistance = distance / (rock.radius * 2.5);
          const rippleFrequency = 22; // Adjusted for better visibility
          
          // Main ripple pattern - stronger amplitude
          const mainRipple = Math.sin(rippleDistance * Math.PI * rippleFrequency) * 0.6;
          
          // Secondary ripples for complexity
          const secondaryRipple = Math.sin(rippleDistance * Math.PI * rippleFrequency * 1.3) * 0.3;
          
          // Fade ripples toward edge
          const edgeFade = Math.pow(1 - rippleDistance, 1.5);
          
          rippleIntensity = Math.max(rippleIntensity, (mainRipple + secondaryRipple) * edgeFade);
        }
      }
      
      // If not near rocks, create flowing ripples
      if (rippleIntensity === 0) {
        // Horizontal flowing ripples
        const horizontalRipple = Math.sin(ny * Math.PI * 22) * 0.4;
        
        // Diagonal ripples
        const diagonalRipple = Math.sin((nx * 0.7 + ny * 0.7) * Math.PI * 15) * 0.2;
        
        // Vary intensity across the surface
        const flowIntensity = 0.3 + 0.7 * Math.sin(nx * Math.PI * 3) * Math.sin(ny * Math.PI * 2);
        
        rippleIntensity = (horizontalRipple) * flowIntensity;
      }
      
      // Apply ripple effect to color
      const rippleEffect = rippleIntensity * 60; // Increased for visibility
      
      r = Math.max(0, Math.min(255, r + rippleEffect));
      g = Math.max(0, Math.min(255, g + rippleEffect));
      b = Math.max(0, Math.min(255, b + rippleEffect));
      
      // Add subtle sand grain texture
      const grain = (noise2D(nx * 80, ny * 80) - 0.5) * 15;
      r = Math.max(0, Math.min(255, r + grain));
      g = Math.max(0, Math.min(255, g + grain));
      b = Math.max(0, Math.min(255, b + grain));
      
      data[index] = r;     // Red
      data[index + 1] = g; // Green
      data[index + 2] = b; // Blue
      data[index + 3] = 255; // Alpha
    }
  }
  
  ctx.putImageData(imageData, 0, 0);
  
  // Create texture from canvas
  const sandTexture = new THREE.CanvasTexture(canvas);
  sandTexture.wrapS = THREE.RepeatWrapping;
  sandTexture.wrapT = THREE.RepeatWrapping;
  sandTexture.repeat.set(1, 1); // Reduced to show more detail
  sandTexture.needsUpdate = true;
  
  return sandTexture;
}

// Create a separate bump map specifically for zen garden ripples
function createZenGardenBumpMap() {
  const bumpSize = 1024;
  const canvas = document.createElement('canvas');
  canvas.width = bumpSize;
  canvas.height = bumpSize;
  const ctx = canvas.getContext('2d');
  
  const imageData = ctx.createImageData(bumpSize, bumpSize);
  const data = imageData.data;
  
  // Same rock positions
  const rockPositions = [
    { x: 0.2, y: 0.4, radius: 0.12 },
    { x: 0.18, y: 0.45, radius: 0.10 },
    { x: 0.35, y: 0.7, radius: 0.11 },
    { x: 0.85, y: 0.8, radius: 0.13 },
    { x: 0.75, y: 0.85, radius: 0.12 }
  ];
  
  for (let x = 0; x < bumpSize; x++) {
    for (let y = 0; y < bumpSize; y++) {
      const index = (y * bumpSize + x) * 4;
      const nx = x / bumpSize;
      const ny = y / bumpSize;
      
      let height = 128; // Middle gray = neutral height
      
      // Add ripple height variations
      let rippleHeight = 0;
      
      for (const rock of rockPositions) {
        const dx = nx - rock.x;
        const dy = ny - rock.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < rock.radius * 2.5) {
          const rippleDistance = distance / (rock.radius * 2.5);
          const rippleFrequency = 12;
          
          const mainRipple = Math.sin(rippleDistance * Math.PI * rippleFrequency) * 0.8;
          const edgeFade = Math.pow(1 - rippleDistance, 1.5);
          
          rippleHeight = Math.max(rippleHeight, mainRipple * edgeFade);
        }
      }
      
      if (rippleHeight === 0) {
        const horizontalRipple = Math.sin(ny * Math.PI * 28) * 0.4;
       // const diagonalRipple = Math.sin((nx * 0.7 + ny * 0.7) * Math.PI * 15) * 0.3;
        const flowIntensity = 0.3 + 0.7 * Math.sin(nx * Math.PI * 3) * Math.sin(ny * Math.PI * 2);
        
        rippleHeight = (horizontalRipple) * flowIntensity;
      }
      
      height += rippleHeight * 80; // Strong height variation
      height = Math.max(0, Math.min(255, height));
      
      data[index] = height;
      data[index + 1] = height;
      data[index + 2] = height;
      data[index + 3] = 255;
    }
  }
  
  ctx.putImageData(imageData, 0, 0);
  
  const bumpTexture = new THREE.CanvasTexture(canvas);
  bumpTexture.wrapS = THREE.RepeatWrapping;
  bumpTexture.wrapT = THREE.RepeatWrapping;
  bumpTexture.repeat.set(1, 1);
  bumpTexture.needsUpdate = true;
  
  return bumpTexture;
}

// stone border
function createZenGardenBorder() {
  const borderGroup = new THREE.Group();
  
  // Border dimensions
  const borderWidth = 900; // Width of zen garden area
  const borderDepth = 980; // Depth of zen garden area
  const wallHeight = 8; // Height above ground
  const wallThickness = 12; // Thickness of walls
  const postHeight = 16; // Corner posts height
  const postSize = 10; // Corner post dimensions
  
  // Stone material for border
  const stoneMaterial = new THREE.MeshStandardMaterial({
    color: 0x6B6B6B, // Gray stone color
    roughness: 0.9,
    metalness: 0.1,
    flatShading: true
  });
  
  // Create the four border walls with gaps for visual interest
  
  // NORTH WALL (back) - with center gap
  const northWall1 = createBorderSection(borderWidth * 0.35, wallHeight, wallThickness);
  northWall1.position.set(-borderWidth * 0.325, wallHeight/2 - 29, borderDepth/2);
  borderGroup.add(northWall1);
  
  const northWall2 = createBorderSection(borderWidth * 0.35, wallHeight, wallThickness);
  northWall2.position.set(borderWidth * 0.325, wallHeight/2 - 29, borderDepth/2);
  borderGroup.add(northWall2);
  
  // SOUTH WALL (front) - with two gaps
  const southWall1 = createBorderSection(borderWidth * 0.25, wallHeight, wallThickness);
  southWall1.position.set(-borderWidth * 0.375, wallHeight/2 - 29, -borderDepth/2);
  borderGroup.add(southWall1);
  
  const southWall2 = createBorderSection(borderWidth * 0.3, wallHeight, wallThickness);
  southWall2.position.set(0, wallHeight/2 - 29, -borderDepth/2);
  borderGroup.add(southWall2);
  
  const southWall3 = createBorderSection(borderWidth * 0.25, wallHeight, wallThickness);
  southWall3.position.set(borderWidth * 0.375, wallHeight/2 - 29, -borderDepth/2);
  borderGroup.add(southWall3);
  
  // EAST WALL (right) - with small gap
  const eastWall1 = createBorderSection(wallThickness, wallHeight, borderDepth * 0.4);
  eastWall1.position.set(borderWidth/2, wallHeight/2 - 29, -borderDepth * 0.3);
  borderGroup.add(eastWall1);
  
  const eastWall2 = createBorderSection(wallThickness, wallHeight, borderDepth * 0.4);
  eastWall2.position.set(borderWidth/2, wallHeight/2 - 29, borderDepth * 0.3);
  borderGroup.add(eastWall2);
  
  // WEST WALL (left) - continuous
  const westWall = createBorderSection(wallThickness, wallHeight, borderDepth);
  westWall.position.set(-borderWidth/2, wallHeight/2 - 29, 0);
  borderGroup.add(westWall);
  
  // Create corner posts
  const cornerPositions = [
    [-borderWidth/2, -borderDepth/2], // Southwest
    [borderWidth/2, -borderDepth/2],  // Southeast  
    [borderWidth/2, borderDepth/2],   // Northeast
    [-borderWidth/2, borderDepth/2]   // Northwest
  ];
  
  cornerPositions.forEach((pos, index) => {
    const post = createCornerPost(postSize, postHeight);
    post.position.set(pos[0], postHeight/2 - 29, pos[1]);
    borderGroup.add(post);
  });
  
  // Add some decorative mid-wall posts
  const midPost1 = createCornerPost(postSize * 0.8, postHeight * 0.8);
  midPost1.position.set(0, postHeight * 0.4 - 29, borderDepth/2);
  borderGroup.add(midPost1);
  
  const midPost2 = createCornerPost(postSize * 0.8, postHeight * 0.8);
  midPost2.position.set(borderWidth/2, postHeight * 0.4 - 29, 0);
  borderGroup.add(midPost2);
  
  app.scene.add(borderGroup);
}

// Helper function to create a border wall section
function createBorderSection(width, height, depth) {
  const sectionGroup = new THREE.Group();
  
  // Create main wall geometry with slight taper for natural look
  const wallGeometry = new THREE.BoxGeometry(width, height, depth, 3, 2, 2);
  
  // Add some irregularity to make it look hand-built
  const positions = wallGeometry.attributes.position;
  for (let i = 0; i < positions.count; i++) {
    const x = positions.getX(i);
    const y = positions.getY(i);
    const z = positions.getZ(i);
    
    // Add slight random displacement, more at the top
    const heightFactor = (y + height/2) / height; // 0 at bottom, 1 at top
    const displacement = (Math.random() - 0.5) * 0.8 * heightFactor;
    
    positions.setX(i, x + displacement);
    positions.setZ(i, z + displacement * 0.5);
  }
  wallGeometry.computeVertexNormals();
  
  // Stone material with color variation
  const stoneMaterial = new THREE.MeshStandardMaterial({
    color: new THREE.Color().setHSL(0.1, 0.1, 0.45 + Math.random() * 0.1), // Slight color variation
    roughness: 0.9,
    metalness: 0.1,
    flatShading: true
  });
  
  const wall = new THREE.Mesh(wallGeometry, stoneMaterial);
  wall.castShadow = true;
  wall.receiveShadow = true;
  
  sectionGroup.add(wall);
  return sectionGroup;
}

// Helper function to create corner posts
function createCornerPost(size, height) {
  const postGroup = new THREE.Group();
  
  // Create post with octagonal cross-section for more interesting shape
  const postGeometry = new THREE.CylinderGeometry(
    size * 0.9, // Top radius (slightly smaller)
    size,       // Bottom radius
    height,     // Height
    8,          // Radial segments (octagonal)
    2           // Height segments
  );
  
  // Add weathering details to posts
  const positions = postGeometry.attributes.position;
  for (let i = 0; i < positions.count; i++) {
    const x = positions.getX(i);
    const y = positions.getY(i);
    const z = positions.getZ(i);
    
    // Add subtle surface irregularities
    const noise = (Math.random() - 0.5) * 0.5;
    const radius = Math.sqrt(x * x + z * z);
    const factor = radius > 0 ? noise / radius : 0;
    
    positions.setX(i, x + x * factor);
    positions.setZ(i, z + z * factor);
  }
  postGeometry.computeVertexNormals();
  
  // Slightly darker stone for posts
  const postMaterial = new THREE.MeshStandardMaterial({
    color: new THREE.Color().setHSL(0.1, 0.15, 0.4 + Math.random() * 0.08),
    roughness: 0.95,
    metalness: 0.05,
    flatShading: true
  });
  
  const post = new THREE.Mesh(postGeometry, postMaterial);
  post.castShadow = true;
  post.receiveShadow = true;
  
  postGroup.add(post);
  
  return postGroup;
}

// distant trees
// --- Simple Perlin noise generator (value noise fallback if you don't want an external lib) ---
function perlinNoise(x) {
  return (
    Math.sin(x * 0.25) * 0.5 +
    Math.sin(x * 0.7) * 0.25 +
    Math.sin(x * 2.3) * 0.15
  );
}

// Builds the distant forest-ring backdrop (a band of treetops that sits just
// inside the mountain ring) and returns the mesh. Toggled via setForestVisible().
function buildForestRing() {
  const radius = 1760;          // inside mountain ring
  const baseHeight = 12;        // baseline tree height
  const segments = 312;         // higher resolution = smoother curve

  const positions = [];
  const colors = [];
  const snowAmt = []; // 0 at the trunk line, 1 at the crowns → snow gathers on the treetops in winter

  const colorBottom = new THREE.Color(0x123a1d); // deep green (kept dark so fog doesn't wash it out)
  const colorTop = new THREE.Color(0x27713f);    // mid green
  // Autumn palette (same as the leaves) — sampled per-fragment as confetti, below.
  const fall = Colors.autumn.map(h => new THREE.Color(h));

  for (let i = 0; i <= segments; i++) {
    const angle = (i / segments) * Math.PI * 2;
    const x = Math.cos(angle) * radius;
    const z = Math.sin(angle) * radius;

    // Multi-octave noise for leafy crown shape
    const lowFreq = perlinNoise(i * 0.1) * 20;   // big treetop waves
    const midFreq = perlinNoise(i * 0.4) * 12;   // tree clumps
    const highFreq = perlinNoise(i * 1.5) * 6;   // leaf detail

    const treetop = baseHeight + lowFreq + midFreq + highFreq * 6.8;
    // Add base + top vertices
    positions.push(x, -38, z);         // bottom (flat line)
    positions.push(x, treetop, z);   // top (leafy silhouette)

    // Colors: bottom dark, top lighter
    const bottomColor = colorBottom.clone();
    colors.push(bottomColor.r, bottomColor.g, bottomColor.b);
    snowAmt.push(0); // bottom (trunk line) — no snow

    const topColor = new THREE.Color().lerpColors(colorBottom, colorTop, 0.7);
    colors.push(topColor.r, topColor.g, topColor.b);
    snowAmt.push(1); // crown — snow gathers here
  }

  // Build geometry as triangle strip
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
  geo.setAttribute("aSnow", new THREE.Float32BufferAttribute(snowAmt, 1));

  const indices = [];
  for (let i = 0; i < segments * 2; i += 2) {
    indices.push(i, i + 1, i + 2);
    indices.push(i + 1, i + 3, i + 2);
  }
  geo.setIndex(indices);
  geo.computeVertexNormals();

  const mat = new THREE.MeshStandardMaterial({
    vertexColors: true,
    side: THREE.DoubleSide,
    flatShading: true
  });
  // Autumn recolors the foliage to the leaf palette as world-space *confetti* —
  // sampled per fragment from a cellular hash so the colors speckle freely across
  // the whole band (a far-off blur of mixed trees), decoupled from the vertex grid
  // so there's no vertical/horizontal banding. Winter then gathers snow on the
  // treetops (pow() biases the white to the crowns; a self-lit lift carries it
  // through the fog, mirroring the mountain caps). uAutumn / uWinter are flipped
  // by setEnvironmentAutumn / setEnvironmentSnow.
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uWinter = { value: _snowOn ? 1 : 0 };
    shader.uniforms.uAutumn = { value: _autumnOn ? 1 : 0 };
    shader.uniforms.uFall = { value: fall }; // vec3[5], the autumn palette
    mat.userData.shader = shader;
    shader.vertexShader = shader.vertexShader
      .replace('void main() {', 'attribute float aSnow;\nvarying float vSnow;\nvarying vec3 vWorldPos;\nvoid main() {')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n  vSnow = aSnow;\n  vWorldPos = (modelMatrix * vec4(transformed, 1.0)).xyz;');
    shader.fragmentShader = shader.fragmentShader
      .replace(
        'void main() {',
        `uniform float uWinter;
        uniform float uAutumn;
        uniform vec3 uFall[5];
        varying float vSnow;
        varying vec3 vWorldPos;
        float fHash(vec3 p){ return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453); }
        // Smooth 3D value noise (trilinear, smoothstep) — continuous so neighbouring
        // fragments land on similar values, grouping alike colors into soft regions.
        float vNoise(vec3 p){
          vec3 i = floor(p), f = fract(p);
          f = f * f * (3.0 - 2.0 * f);
          float n000 = fHash(i), n100 = fHash(i + vec3(1.0, 0.0, 0.0));
          float n010 = fHash(i + vec3(0.0, 1.0, 0.0)), n110 = fHash(i + vec3(1.0, 1.0, 0.0));
          float n001 = fHash(i + vec3(0.0, 0.0, 1.0)), n101 = fHash(i + vec3(1.0, 0.0, 1.0));
          float n011 = fHash(i + vec3(0.0, 1.0, 1.0)), n111 = fHash(i + vec3(1.0, 1.0, 1.0));
          return mix(
            mix(mix(n000, n100, f.x), mix(n010, n110, f.x), f.y),
            mix(mix(n001, n101, f.x), mix(n011, n111, f.x), f.y),
            f.z);
        }
        void main() {`
      )
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
        if (uAutumn > 0.0) {
          // Smooth color field instead of hard cells: a broad region noise groups
          // alike colors into drifts, a finer octave breaks them up, and we blend
          // between the two neighbouring palette entries (no hard floor/ceil) so the
          // flecks melt into each other.
          vec3 wp = vWorldPos;
          float field = vNoise(wp * 0.013) * 0.72 + vNoise(wp * 0.11) * 0.28;
          float t = clamp(field, 0.0, 1.0) * 3.9999;   // 0..(N-1) palette coordinate
          int lo = int(floor(t));
          float fr = t - float(lo);
          vec3 cLo = uFall[0], cHi = uFall[0];
          for (int k = 0; k < 5; k++) {
            if (k == lo) cLo = uFall[k];
            if (k == lo + 1) cHi = uFall[k];
          }
          vec3 confetti = mix(cLo, cHi, fr);            // soft blend between neighbours
          confetti *= 0.82 + 0.28 * vNoise(wp * 0.18 + 19.3); // gentle brightness drift
          diffuseColor.rgb = mix(diffuseColor.rgb, confetti, uAutumn);
        }
        float snowCap = clamp(uWinter * pow(vSnow, 3.0), 0.0, 1.0);
        diffuseColor.rgb = mix(diffuseColor.rgb, vec3(1.0), snowCap);`
      )
      .replace(
        '#include <emissivemap_fragment>',
        '#include <emissivemap_fragment>\n  totalEmissiveRadiance += vec3(0.45, 0.48, 0.52) * snowCap;'
      );
  };

  const treeRing = new THREE.Mesh(geo, mat);
  treeRing.position.y = -10;
  return treeRing;
}

// --- Optional scene backdrops (toggled from the settings drawer) ---
// Lazily built on first enable, then cached on app.backdrops so subsequent
// toggles are just a cheap .visible flip — no rebuild, no flicker.
function setMountainsVisible(visible) {
  if (visible && !app.backdrops.mountains) {
    app.backdrops.mountains = buildMountainRing();
    app.scene.add(app.backdrops.mountains);
  }
  if (app.backdrops.mountains) app.backdrops.mountains.visible = visible;
  refreshBackdropGround();
}

// Lone Mt-Fuji peak behind the mountain range. Reads best with Mountains on, but
// stands on its own — and unlike the rings it does NOT pull up the distant grass
// disc (its skirt just recedes into the fog), so selecting only Fuji is cheap.
function setFujiVisible(visible) {
  if (visible && !app.backdrops.fuji) {
    app.backdrops.fuji = buildFujiMountain();
    app.scene.add(app.backdrops.fuji);
  }
  if (app.backdrops.fuji) app.backdrops.fuji.visible = visible;
}

function setForestVisible(visible) {
  if (visible && !app.backdrops.forest) {
    app.backdrops.forest = buildForestRing();
    app.scene.add(app.backdrops.forest);
  }
  if (app.backdrops.forest) app.backdrops.forest.visible = visible;
  refreshBackdropGround();
}

// Distant pagoda backdrop: a GLB landmark set far out so it reads as part of the
// horizon, not the garden. Unlike the mountain/forest rings it stands on its own
// and does NOT pull up the distant ground disc — it can sit against the bare sky.
// It's a loaded model rather than generated geometry, so the group is added
// immediately and the mesh dropped in when the async load resolves.
function setPagodaVisible(visible) {
  if (visible && !app.backdrops.pagoda) {
    const group = new THREE.Group();
    app.backdrops.pagoda = group;
    app.scene.add(group);
    loader.load('assets/models/Pagoda.glb', function (gltf) {
      const model = gltf.scene;
      // Measure the real bounding box and scale to a target height, then seat it
      // on the ground — robust to whatever units/transforms the GLB ships with.
      const targetHeight = 280; // tall enough to read across the distance
      const size = new THREE.Vector3();
      new THREE.Box3().setFromObject(model).getSize(size);
      model.scale.setScalar(targetHeight / size.y);

      const scaledBox = new THREE.Box3().setFromObject(model);
      // Far off toward the back-left, well beyond the garden, inside the rings.
      model.position.set(-900, -30 - scaledBox.min.y, 1200);
      model.rotation.y = Math.PI * 0.85; // angle its face back toward the garden
      model.traverse(function (child) {
        if (child.isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
      group.add(model);
    });
  }
  if (app.backdrops.pagoda) app.backdrops.pagoda.visible = visible;
}

// Courtyard backdrop: a full enclosure (walls, pillars, roof, garden floor) that
// wraps AROUND the bonsai, with the camera living inside it. Unlike the other
// backdrops it isn't a distant accent — it's scaled to a large interior footprint
// so the camera (orbits up to maxDistance ~500 from the target) stays within the
// walls, then centered on the tree and seated on the ground. Its GLB materials
// are already double-sided, so the interior faces render correctly from inside.
function setCourtyardVisible(visible) {
  if (visible && !app.backdrops.courtyard) {
    const group = new THREE.Group();
    app.backdrops.courtyard = group;
    app.scene.add(group);
    loader.load('assets/models/courtyard.glb', function (gltf) {
      const model = gltf.scene;
      // Scale by horizontal footprint (not height): the interior must clear the
      // camera's orbit radius. 1700 wide → ~850 half-extent, well past the 500 max.
      const targetFootprint = 1330;
      const box = new THREE.Box3().setFromObject(model);
      const size = new THREE.Vector3();
      box.getSize(size);
      model.scale.setScalar(targetFootprint / Math.max(size.x, size.z));

      // Re-measure, then center the enclosure on the tree (origin) and rest its
      // floor on the ground plane (y = -30).
      const scaledBox = new THREE.Box3().setFromObject(model);
      const center = new THREE.Vector3();
      scaledBox.getCenter(center);
      model.position.set(-center.x, -30 - scaledBox.min.y, -center.z);

      model.traverse(function (child) {
        if (child.isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
      group.add(model);
    });
  }
  if (app.backdrops.courtyard) app.backdrops.courtyard.visible = visible;
}

// House backdrop: a GLB dwelling set just beyond the zen-garden border so it reads
// as the home the garden belongs to, standing off the back-right corner. Same lazy
// load + measure-and-seat pattern as the pagoda — added immediately, the mesh
// dropped in when the async load resolves. Sits on its own (no distant ground disc).
function setHouseVisible(visible) {
  if (visible && !app.backdrops.house) {
    const group = new THREE.Group();
    app.backdrops.house = group;
    app.scene.add(group);
    loader.load('assets/models/house.glb', function (gltf) {
      const model = gltf.scene;
      // Measure the real bounding box and scale to a target height, then seat it
      // on the ground — robust to whatever units/transforms the GLB ships with.
      const targetHeight = 365; // world units; a dwelling taller than the bonsai
      const size = new THREE.Vector3();
      new THREE.Box3().setFromObject(model).getSize(size);
      model.scale.setScalar(targetHeight / size.y);

      // Re-measure after scaling to drop its base onto the ground (y = -30). Set
      // just past the garden border (~900x980, half-depth 490) off the back-right.
      const scaledBox = new THREE.Box3().setFromObject(model);
      model.position.set(766, -60 - scaledBox.min.y, 360);
      model.rotation.y = 136.75; // face the house back toward the garden
      model.traverse(function (child) {
        if (child.isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;
          // The windows are the glass mesh (Material_14). Warm its emissive and
          // register it so the night cue (setLanternNight) makes them glow after
          // dark — color baked in here, intensity ramped 0→full by the day cycle.
          if (child.material && child.material.name === 'Material_14') {
            child.material.emissive = new THREE.Color(0xFFB04A);
            child.material.emissiveIntensity = 0;
            houseWindowMats.push(child.material);
          }
        }
      });
      applyHouseNight(lanternNightK); // light them now if the house loads after dark
      group.add(model);
    });
  }
  if (app.backdrops.house) app.backdrops.house.visible = visible;
  // The Shiba pup lives at the house: sits on the deck, then roams the yard.
  setShibaActive(visible);
}

// --- Lantern firebox glow (lit after dark) --------------------------------
// Both stone lanterns light up at night. The garden's night mood drives this:
// main.js calls setLanternNight(k) with a 0→1 night factor (0 = day) from the
// same path that fades the uplights in, so the lanterns track dusk and the day
// cycle automatically. Each lantern's light + glow core is parented to its group,
// so toggling the lantern off in Settings also removes its light.
const lanternGlows = []; // { light, glowMat, intensity }
let lanternNightK = 0;   // last night factor applied; reused when a lantern loads mid-night

// House windows lit warm at night, driven by the same night factor as the lanterns
// (setLanternNight). Each entry is a glass material whose emissive we ramp up after
// dark; the warm color is baked in at load, the intensity tracks dusk/cycle.
const houseWindowMats = []; // glass materials made to glow warm after dark
const HOUSE_GLOW_MAX = 2.4; // emissiveIntensity at full night (0 by day)

function applyHouseNight(k) {
  for (const m of houseWindowMats) m.emissiveIntensity = HOUSE_GLOW_MAX * k;
}

function applyLanternGlow(g, k) {
  g.light.intensity = g.intensity * k;
  g.glowMat.opacity = 0.9 * k;
}

// Seat a warm PointLight (plus a small additive core that makes the firebox read
// as lit) in a loaded lantern model's lamp housing, parented to `group`. Measures
// the model so the source lands in the firebox regardless of the model's scale.
function addLanternGlow(group, model, opts) {
  const box = new THREE.Box3().setFromObject(model);
  const size = new THREE.Vector3(); box.getSize(size);
  const center = new THREE.Vector3(); box.getCenter(center);
  // The firebox sits in the upper third of a stone lantern — seat the source there.
  const pos = new THREE.Vector3(center.x, box.min.y + size.y * 0.62, center.z);

  const light = new THREE.PointLight(opts.color, 0, opts.distance, 2);
  light.position.copy(pos);
  group.add(light);

  // Additive core: the firebox itself glowing (the PointLight handles spill onto
  // the surrounding stone + sand). depthWrite off + non-raycastable so it never
  // occludes the scene or the prune/shape pick.
  const glowMat = new THREE.MeshBasicMaterial({
    color: opts.color, transparent: true, opacity: 0,
    depthWrite: false, blending: THREE.AdditiveBlending,
  });
  const glow = new THREE.Mesh(new THREE.SphereGeometry(opts.glowRadius, 12, 10), glowMat);
  glow.position.copy(pos);
  glow.raycast = () => {};
  group.add(glow);

  const g = { light, glowMat, intensity: opts.intensity };
  lanternGlows.push(g);
  applyLanternGlow(g, lanternNightK); // light it now if it's switched on after dark
}

// Drive every lantern firebox from the current night factor (0 = day, 1 = night).
// Called by main.js's night mood; also remembers the factor so a lantern toggled
// on later in the night comes up already lit.
function setLanternNight(k) {
  lanternNightK = k;
  for (const g of lanternGlows) applyLanternGlow(g, k);
  applyHouseNight(k); // the house windows light up on the same dusk/cycle
}

// Decorative stone lantern, off by default and toggled from the settings drawer.
// Like the backdrops it's lazily loaded on first enable, then cached so later
// toggles are a cheap .visible flip. Sits on the sand off to the side of the
// pebble zen-garden area (clear of the bonsai pot at the origin).
function setLanternVisible(visible) {
  if (visible && !lantern) {
    lantern = new THREE.Group();
    app.scene.add(lantern);
    loader.load('assets/models/Japanese-Stone-Lamp.glb', function (gltf) {
      const model = gltf.scene;
      const scale = 32; // model is ~3.2 units tall; this reads as a garden feature
      model.scale.setScalar(scale);
      // Model base sits at y = -2.19 in local space; lift so it rests on the sand.
      model.position.set(288, -29 + 2.19 * scale, -186);
      model.rotation.y = -Math.PI * 0.25; // angle its lit face toward the bonsai
      model.traverse(function (child) {
        if (child.isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
      lantern.add(model);
      // Warm firebox glow, lit at night (tall lantern → larger reach).
      addLanternGlow(lantern, model, { color: 0xFFB05A, intensity: 450, distance: 380, glowRadius: 5 });
    });
  }
  if (lantern) lantern.visible = visible;
}

// Decorative torii gate, off by default and toggled from the settings drawer.
// Same lazy load + cache pattern as the lantern, but placed OUTSIDE the zen
// garden border (~900x980), standing on the ground behind the garden so it
// frames the bonsai from a distance.
function setToriiVisible(visible) {
  if (visible && !torii) {
    torii = new THREE.Group();
    app.scene.add(torii);
    loader.load('assets/models/Torii-Gate.glb', function (gltf) {
      const model = gltf.scene;
      // The GLB bakes a scale-100 node transform onto the mesh, so its real size
      // isn't obvious from the raw geometry. Measure the actual bounding box and
      // scale to a target height, then seat it on the ground — no magic numbers.
      const targetHeight = 190; // world units; a gateway taller than the bonsai
      const box = new THREE.Box3().setFromObject(model);
      const size = new THREE.Vector3();
      box.getSize(size);
      model.scale.setScalar(targetHeight / size.y);

      // Re-measure after scaling to drop the feet exactly onto the ground.
      const scaledBox = new THREE.Box3().setFromObject(model);
      model.position.set(0, -30 - scaledBox.min.y, 700); // beyond the garden border
      model.rotation.y = Math.PI; // face the gateway back toward the garden

      model.traverse(function (child) {
        if (child.isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
      torii.add(model);
    });
  }
  if (torii) torii.visible = visible;
}

// Decorative short stone lantern, off by default and toggled from the settings
// drawer. Same lazy load + cache pattern as the tall lantern, but a squat piece
// that sits low ON the sand inside the zen garden (clear of the bonsai pot at
// the origin). Measures its real bounding box to size and seat it — no guesses.
function setShortLanternVisible(visible) {
  if (visible && !shortLantern) {
    shortLantern = new THREE.Group();
    app.scene.add(shortLantern);
    loader.load('assets/models/short-stone-lantern.glb', function (gltf) {
      const model = gltf.scene;
      const targetHeight = 40; // world units; a low accent on the sand
      const size = new THREE.Vector3();
      new THREE.Box3().setFromObject(model).getSize(size);
      model.scale.setScalar(targetHeight / size.y);

      // Re-measure after scaling to rest its base on the sand (y = -29).
      const scaledBox = new THREE.Box3().setFromObject(model);
      model.position.set(-210, -29 - scaledBox.min.y, 210); // inside the garden
      model.rotation.y = Math.PI * 0.2;
      model.traverse(function (child) {
        if (child.isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
      shortLantern.add(model);
      // Warm firebox glow, lit at night (short lantern → a smaller, lower pool).
      addLanternGlow(shortLantern, model, { color: 0xFFB05A, intensity: 250, distance: 250, glowRadius: 4 });
    });
  }
  if (shortLantern) shortLantern.visible = visible;
}

// Decorative watering can, off by default and toggled from the settings drawer.
// Same lazy load + cache pattern as the lanterns — a small playful prop resting on
// the sand inside the zen garden, set front-right and clear of the bonsai pot at
// the origin and the other accents (tall lantern back-right, short lantern
// front-left). Measures its real bounding box to size and seat it — no guesses.
function setWaterCanVisible(visible) {
  if (visible && !waterCan) {
    waterCan = new THREE.Group();
    app.scene.add(waterCan);
    loader.load('assets/models/water-can.glb', function (gltf) {
      const model = gltf.scene;
      const targetHeight = 30; // world units; a small accent on the sand
      const size = new THREE.Vector3();
      new THREE.Box3().setFromObject(model).getSize(size);
      model.scale.setScalar(targetHeight / size.y);

      // Re-measure after scaling to rest its base on the sand (y = -29).
      const scaledBox = new THREE.Box3().setFromObject(model);
      model.position.set(190, -29 - scaledBox.min.y, 150); // inside the garden, front-right
      model.rotation.y = -Math.PI * 0.35; // angle the spout toward the bonsai
      model.traverse(function (child) {
        if (child.isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
      waterCan.add(model);
    });
  }
  if (waterCan) waterCan.visible = visible;
}

// Decorative stool, off by default and toggled from the settings drawer. Same
// lazy load + cache pattern as the other props — a small accent set close to the
// bonsai on its front-left, clear of the pot at the origin and the other accents
// (water can front-right, short lantern further front-left). Measures its real
// bounding box to size and seat it — no guesses.
function setStoolVisible(visible) {
  if (visible && !stool) {
    stool = new THREE.Group();
    app.scene.add(stool);
    loader.load('assets/models/stool.glb', function (gltf) {
      const model = gltf.scene;
      const size = new THREE.Vector3();
      new THREE.Box3().setFromObject(model).getSize(size);
      model.scale.setScalar(STOOL_HEIGHT / size.y);

      // Re-measure after scaling to rest its base on the sand (y = -29).
      const scaledBox = new THREE.Box3().setFromObject(model);
      model.position.set(STOOL_POS.x, -29 - scaledBox.min.y, STOOL_POS.z);
      model.rotation.y = Math.PI * 0.15;
      model.traverse(function (child) {
        if (child.isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
      stool.add(model);
    });
  }
  if (stool) stool.visible = visible;
}

// Decorative tea set, off by default and toggled from the settings drawer. Rests
// on top of the stool (see STOOL_POS/STOOL_TOP_Y) so the two together read as the
// player's seat with a tea break set out — meant to be enabled alongside the
// Stool toggle. Same lazy load + cache pattern as the other props; seats the set
// on the stool's *known* top rather than a guessed height.
function setTeaVisible(visible) {
  if (visible && !tea) {
    tea = new THREE.Group();
    app.scene.add(tea);
    loader.load('assets/models/teaset.glb', function (gltf) {
      const teaSet = gltf.scene;
      const teaHeight = 16.5; // small prop sitting on the stool
      const teaSize = new THREE.Vector3();
      new THREE.Box3().setFromObject(teaSet).getSize(teaSize);
      teaSet.scale.setScalar(teaHeight / teaSize.y);

      // Re-measure after scaling to rest its base on the stool top.
      const teaBox = new THREE.Box3().setFromObject(teaSet);
      teaSet.position.set(STOOL_POS.x + 4.5, STOOL_TOP_Y - teaBox.min.y, STOOL_POS.z);
      teaSet.traverse(function (child) {
        if (child.isMesh) { child.castShadow = true; child.receiveShadow = true; }
      });
      tea.add(teaSet);
    });
  }
  if (tea) tea.visible = visible;
}

// Decorative bamboo clump in the back-left corner of the zen garden, with a moss
// patch laid on the sand beneath it. Off by default, toggled from the settings
// drawer; same lazy load + cache pattern as the other props. The moss is a flat
// textured disc built immediately (its moss.jpg loads in); the bamboo GLB loads
// in and is seated base-on-sand and centered over the moss.
function setBambooVisible(visible) {
  if (visible && !bamboo) {
    bamboo = new THREE.Group();
    app.scene.add(bamboo);
    const spotX = -334, spotZ = -340; // back-left corner, clear of the bonsai and stool

    // Moss mound on the sand: a flattened sphere sunk so its equator sits at the
    // sand line (y = -29) — the lower half hides under the opaque sand, the upper
    // half rises as a low dome. Per-vertex noise lumps the surface so it reads as
    // a soft moss cushion rather than a smooth bubble.
    const mossTexture = new THREE.TextureLoader().load('assets/textures/moss.jpg');
    mossTexture.wrapS = THREE.RepeatWrapping;
    mossTexture.wrapT = THREE.RepeatWrapping;
    mossTexture.repeat.set(4, 4);
    mossTexture.colorSpace = THREE.SRGBColorSpace;
    const mossRadius = 60, mossHeight = 10; // footprint radius and how far it rises
    const mossGeo = new THREE.SphereGeometry(mossRadius, 48, 24);
    const mp = mossGeo.attributes.position;
    const mv = new THREE.Vector3();
    for (let i = 0; i < mp.count; i++) {
      mv.fromBufferAttribute(mp, i);
      const lump = 1 + (noise2D(mv.x * 0.02, mv.z * 0.02) - 0.2) * 0.02; // faint, smooth variation
      mp.setXYZ(i, mv.x * lump, mv.y, mv.z * lump);
    }
    mossGeo.computeVertexNormals();
    const moss = new THREE.Mesh(
      mossGeo,
      new THREE.MeshStandardMaterial({ map: mossTexture, roughness: 0.95, metalness: 0 })
    );
    moss.scale.set(1, mossHeight / mossRadius, 1); // flatten the sphere into a low mound
    moss.position.set(spotX, -29, spotZ); // equator at sand level; lower half buried
    moss.receiveShadow = true;
    moss.castShadow = true;
    bamboo.add(moss);

    loader.load('assets/models/bamboo.glb', function (gltf) {
      const model = gltf.scene;
      const targetHeight = 220; // world units; a tall corner accent
      const size = new THREE.Vector3();
      new THREE.Box3().setFromObject(model).getSize(size);
      model.scale.setScalar(targetHeight / size.y);

      // Re-measure after scaling to seat its base near the top of the moss mound
      // (sand level + mound height), so the bamboo rises from the crown.
      const scaledBox = new THREE.Box3().setFromObject(model);
      model.position.set(spotX, -29 + mossHeight - scaledBox.min.y - 8, spotZ);
      const baseY = model.position.y + scaledBox.min.y; // world-Y the base sits at (sway pivot)
      model.rotation.y = Math.PI * 0.25;

      // Bend both clumps in the wind off the SAME shared uniforms as the bonsai
      // foliage, so they sway in time with the leaves. Inject once per unique
      // material; the clone shares these materials so the shader covers it too.
      const swayed = new Set();
      const addBamboo = function (m) {
        m.traverse(function (child) {
          if (!child.isMesh) return;
          child.castShadow = true;
          child.receiveShadow = true;
          const mats = Array.isArray(child.material) ? child.material : [child.material];
          mats.forEach(function (mat) {
            if (swayed.has(mat)) return;
            swayed.add(mat);
            injectWindSway(mat, { baseY, height: targetHeight, amp: 22 });
            mat.needsUpdate = true;
          });
        });
        bamboo.add(m);
      };
      addBamboo(model);

      // A second clump, same height, nudged over to the right (and a touch
      // forward + rotated) so it reads as a natural pair rather than a copy.
      const model2 = model.clone();
      model2.position.set(spotX + 17, model.position.y - 28, spotZ + 6);
      model2.rotation.y = 26; 
      addBamboo(model2);
    });
  }
  if (bamboo) bamboo.visible = visible;
}

// Show the distant grass disc only while a ring backdrop is up, so the rings
// have land to stand on; hide it again when both are off.
function refreshBackdropGround() {
  if (!app.backdrops.ground) return;
  const ringUp = !!(app.backdrops.mountains && app.backdrops.mountains.visible) ||
                 !!(app.backdrops.forest && app.backdrops.forest.visible);
  app.backdrops.ground.visible = ringUp;
  // Wild deer roam the meadow whenever a distant ring (forest or mountains) is up.
  setDeerActive(ringUp);
}

// Updated createEnvironment function
function createEnvironment() {
  // Registry for the optional, lazily-built backdrops (see setMountainsVisible/
  // setForestVisible). Off by default — the user opts in from the settings drawer.
  app.backdrops = { mountains: null, fuji: null, forest: null, pagoda: null, courtyard: null, ground: null };

  // Ground geometry and material (keep your existing grass ground)
  const groundGeo = new THREE.CircleGeometry(2400, 128);
  // No tiled photo here: at 2400-radius the repeat collapses into noise *with*
  // visible seam lines. Instead the green variation is generated procedurally in
  // the shader from continuous fbm noise driven by world position — so it never
  // repeats and has no seams. roughness/metalness keep it matte like turf.
  const groundMat = new THREE.MeshStandardMaterial({
    color: 0xffffff, // base is set in the shader; leave this neutral so it doesn't tint
    roughness: 1,
    metalness: 0,
  });
  groundMat.onBeforeCompile = (shader) => {
    // uWinter blankets the grass in snow (flipped by setEnvironmentSnow);
    // uAutumn dries it to brown (flipped by setEnvironmentAutumn).
    shader.uniforms.uWinter = { value: _snowOn ? 1 : 0 };
    shader.uniforms.uAutumn = { value: _autumnOn ? 1 : 0 };
    groundShader = shader;
    // Pass world XZ down to the fragment shader so the noise is locked to the
    // ground plane (seamless across the whole disc, independent of UVs).
    shader.vertexShader = shader.vertexShader
      .replace('void main() {', 'varying vec3 vWorldPos;\nvoid main() {')
      .replace(
        '#include <begin_vertex>',
        '#include <begin_vertex>\n  vWorldPos = (modelMatrix * vec4(transformed, 1.0)).xyz;'
      );
    shader.fragmentShader = shader.fragmentShader
      .replace(
        'void main() {',
        `varying vec3 vWorldPos;
        uniform float uWinter;
        uniform float uAutumn;
        float gHash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
        float gNoise(vec2 p){
          vec2 i = floor(p), f = fract(p);
          vec2 u = f * f * (3.0 - 2.0 * f);
          float a = gHash(i), b = gHash(i + vec2(1.0, 0.0));
          float c = gHash(i + vec2(0.0, 1.0)), d = gHash(i + vec2(1.0, 1.0));
          return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
        }
        float gFbm(vec2 p){
          float v = 0.0, amp = 0.5;
          for (int i = 0; i < 5; i++){ v += amp * gNoise(p); p *= 2.0; amp *= 0.5; }
          return v;
        }
        void main() {`
      )
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
        vec2 gp = vWorldPos.xz;
        float patches = gFbm(gp * 0.012);          // broad meadow patches
        float detail  = gFbm(gp * 0.13 + 17.0);    // finer grassy break-up
        vec3 grassDark = vec3(0.13, 0.28, 0.07);
        vec3 grassLite = vec3(0.24, 0.40, 0.14);
        vec3 grass = mix(grassDark, grassLite, smoothstep(0.25, 0.75, patches));
        grass *= 0.80 + 0.30 * detail;             // subtle brightness mottle
        // Autumn: dry the meadow to a warm golden hue (mottle preserved). Amber
        // hollows lifting to bright wheat-gold on the rises.
        vec3 dryDark = vec3(0.46, 0.30, 0.10);
        vec3 dryLite = vec3(0.82, 0.62, 0.22);
        vec3 dry = mix(dryDark, dryLite, smoothstep(0.25, 0.75, patches)) * (0.90 + 0.22 * detail);
        grass = mix(grass, dry, uAutumn);
        // Winter: blanket the meadow in snow, keeping the mottle so it isn't flat.
        // Applied last so snow wins if both ever read true.
        vec3 snow = vec3(0.87, 0.91, 0.96) * (0.90 + 0.14 * detail);
        grass = mix(grass, snow, uWinter);
        diffuseColor.rgb = grass;`
      );
  };
  
  const ground = new THREE.Mesh(groundGeo, groundMat);
  ground.rotation.x = -Math.PI / 2;
  ground.position.y = -30;
  ground.receiveShadow = true;
  // The distant grass disc is the land the mountain/forest rings stand on, so
  // it's part of the backdrop system (see refreshBackdropGround) rather than
  // always-on — hidden by default to keep the bare zen-garden look.
  ground.visible = false;
  app.backdrops.ground = ground;
  app.scene.add(ground);

  // Load the pebble normal map
  const textureLoader = new THREE.TextureLoader();
  const normalMap = textureLoader.load('assets/textures/small-pebble-map.png');
  normalMap.wrapS = THREE.RepeatWrapping;
  normalMap.wrapT = THREE.RepeatWrapping;
  normalMap.repeat.set(12, 12);
  normalMap.minFilter = THREE.LinearMipmapLinearFilter;
  normalMap.magFilter = THREE.LinearFilter;
  normalMap.generateMipmaps = true;

  // Zen garden texture with both normal map and zen ripple bump map
  const textureGeometry = new THREE.PlaneGeometry(900, 980);
  const textureMaterial = new THREE.MeshStandardMaterial({
    map: createZenGardenTexture(),
    normalMap: normalMap, // Pebble texture for fine detail
    normalScale: new THREE.Vector2(8, 8),
    bumpMap: createZenGardenBumpMap(), // Zen ripples for large patterns
    bumpScale: 12, // Slightly reduced to balance with normal map
    roughness: 0.9,
    metalness: 0.05,
  });
  
  const texture = new THREE.Mesh(textureGeometry, textureMaterial);
  texture.rotation.x = -Math.PI / 2;
  texture.position.y = -29;
  texture.receiveShadow = true;
  gravel = texture;
  setGravelDark(gravelDark); // honor the toggle's restored state on (re)build
  app.scene.add(texture);
  
  // Pot, rocks, mountains, etc.
  createPot();
  // addBackgroundRocks(); // random scattered rocks — too cluttered next to the zen garden; revisit later
  addZenGardenRocks();
  createZenGardenBorder();
  addPebble();
  // Mountain/forest backdrops are opt-in via the settings drawer (setMountainsVisible/setForestVisible).
}

// noise2D function
function noise2D(x, y) {
  let n = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453;
  return 2.0 * (n - Math.floor(n)) - 1.0;
}

// POT 
function createPot() {
  if (pot) {
    app.scene.remove(pot);
  }

  const potGroup = new THREE.Group();

  // Pot dimensions
  const potTopWidth = 56;
  const potTopDepth = 38;
  const potBottomWidth = 45; // Smaller for taper
  const potBottomDepth = 30;
  const potHeight = 13;
  const wallThickness = 1;

  // Create main pot body using a custom geometry approach
  const potBodyGeometry = createTaperedBoxGeometry(
    potTopWidth, potTopDepth,
    potBottomWidth, potBottomDepth,
    potHeight
  );

  const potMaterial = new THREE.MeshStandardMaterial({
    color: 0x8B4513,
    roughness: 0.8,
    metalness: 0.1,
    flatShading: true
  });

  const potBody = new THREE.Mesh(potBodyGeometry, potMaterial);
  potBody.position.y = -potHeight / 2;
  potBody.castShadow = true;
  potBody.receiveShadow = true;

  // Create interior space (slightly smaller)
  const interiorGeometry = createTaperedBoxGeometry(
    potTopWidth - wallThickness * 2, potTopDepth - wallThickness * 2,
    potBottomWidth - wallThickness * 2, potBottomDepth - wallThickness * 2,
    potHeight - wallThickness
  );

  const interiorMaterial = new THREE.MeshStandardMaterial({
    color: 0x654321,
    roughness: 0.9,
    metalness: 0.05,
    flatShading: true,
    side: THREE.BackSide
  });

  const interior = new THREE.Mesh(interiorGeometry, interiorMaterial);
  interior.position.y = -potHeight / 2 + wallThickness / 2;

  // Create top band (decorative rim) - properly sized
  const topBandGeometry = new THREE.BoxGeometry(
    potTopWidth + 2, // Width to match pot top
    2, // Height (thin flat band)
    potTopDepth + 2, // Depth to match pot top  
    4, 1, 4 // Segments for low-poly look
  );
  
  const bandMaterial = new THREE.MeshStandardMaterial({
    color: 0x654321,
    roughness: 0.7,
    metalness: 0.2,
    flatShading: true
  });

  const topBand = new THREE.Mesh(topBandGeometry, bandMaterial);
  topBand.position.y = -1.1; // Position at top of pot

  // Create bottom band - properly sized
  const bottomBandGeometry = new THREE.BoxGeometry(
    potBottomWidth + 2, // Width to match pot bottom
    3, // Height (thin flat band)
    potBottomDepth + 2, // Depth to match pot bottom
    4, 1, 4 // Segments
  );

  const bottomBand = new THREE.Mesh(bottomBandGeometry, bandMaterial);
  bottomBand.position.y = -potHeight + 1; // Position at bottom of pot

  // Create soil with texture
  const soilGeometry = new THREE.BoxGeometry(
    potTopWidth - 3, 2.3, potTopDepth - 3,
    6, 6, 7, 9, // Segments for texture
    true, // Closed
  );
  
  // Add bumpy texture to soil
  const soilPositions = soilGeometry.attributes.position;
  for (let i = 0; i < soilPositions.count; i++) {
    const y = soilPositions.getY(i);
    if (y > 0) {
      // Only modify top vertices
      soilPositions.setY(i, y + (Math.random() - 0.5) * 2);
    }
  }
  soilGeometry.computeVertexNormals();

  const soilMaterial = new THREE.MeshStandardMaterial({
    color: 0x302A27,
    roughness: 1.3,
    flatShading: true
  });

  // Create flat filler soil (no bumps)
const flatSoilGeometry = new THREE.BoxGeometry(
  potTopWidth - 3, -.5, potTopDepth - 3,
  1, 1, 1 // fewer segments since we don't need bumps
);

const flatSoilMaterial = new THREE.MeshStandardMaterial({
  color: 0x3a322f, // slightly different shade
  roughness: 1.2,
  flatShading: true
});

const flatSoil = new THREE.Mesh(flatSoilGeometry, flatSoilMaterial);
flatSoil.position.y = 8.5; // position just above the bumpy soil
flatSoil.receiveShadow = true;
app.scene.add(flatSoil);

  const soil = new THREE.Mesh(soilGeometry, soilMaterial);
  soil.position.y = -.5;
  soil.receiveShadow = true;

  // Create cylindrical legs
  const legGeometry = new THREE.CylinderGeometry(2.5, 2.5, 4, 8);
  const legMaterial = new THREE.MeshStandardMaterial({
    color: 0x654321,
    roughness: 0.8,
    flatShading: true
  });

  const legPositions = [
    [potBottomWidth/2 - 4, -potHeight - 2, potBottomDepth/2 - 4],
    [-potBottomWidth/2 + 4, -potHeight - 2, potBottomDepth/2 - 4],
    [potBottomWidth/2 - 4, -potHeight - 2, -potBottomDepth/2 + 4],
    [-potBottomWidth/2 + 4, -potHeight - 2, -potBottomDepth/2 + 4]
  ];

  legPositions.forEach(pos => {
    const leg = new THREE.Mesh(legGeometry, legMaterial);
    leg.position.set(...pos);
    leg.castShadow = true;
    potGroup.add(leg);
  });

  // Add rocks in soil (matching environmental rock colors)
  // Add rocks in soil (matching environmental rock colors) - made bigger
  const rockColors = [0x5f5f5f, 0x707070, 0x404040, 0x808080];
  
  for (let i = 0; i < 6; i++) {
    const rockSize = Math.random() * 3.5 + 2; // Increased from 2.5 + 1
    const rockType = Math.floor(Math.random() * 3);
    let rockGeometry;

    switch (rockType) {
      case 0:
        rockGeometry = new THREE.TetrahedronGeometry(rockSize);
        break;
      case 1:
        rockGeometry = new THREE.OctahedronGeometry(rockSize);
        break;
      case 2:
        rockGeometry = new THREE.IcosahedronGeometry(rockSize, 0);
        break;
    }

    const rockColor = rockColors[Math.floor(Math.random() * rockColors.length)];
    const rockMaterial = new THREE.MeshStandardMaterial({
      color: rockColor,
      roughness: 0.9,
      flatShading: true
    });
    registerRock(rockMaterial, 'rock-fracture-normal.jpg'); // smaller soil rocks: fractured grain

    const rock = new THREE.Mesh(rockGeometry, rockMaterial);
    rock.position.set(
      (Math.random() - 0.5) * (potTopWidth - 20),
      0, // Adjusted to sit on soil surface
      (Math.random() - 0.5) * (potTopDepth - 20)
    );
    
    rock.scale.set(
      1 + Math.random() * 0.5,
      0.7 + Math.random() * 0.4,
      1 + Math.random() * 0.5
    );
    
    rock.rotation.set(
      Math.random() * Math.PI,
      Math.random() * Math.PI,
      Math.random() * Math.PI
    );
    
    rock.castShadow = true;
    potGroup.add(rock);
  }

  // Assemble the pot
  potGroup.add(potBody);
  potGroup.add(interior);
  potGroup.add(topBand);
  potGroup.add(bottomBand);
  potGroup.add(soil);
  potGroup.position.y = 8;
  app.scene.add(potGroup);
  pot = potGroup;
}

// Helper function to create a tapered box geometry
function createTaperedBoxGeometry(topW, topD, bottomW, bottomD, height) {
  const geometry = new THREE.BufferGeometry();
  
  // Define vertices for a tapered box
  const vertices = [
    // Bottom face (y = -height/2)
    -bottomW/2, -height/2, -bottomD/2,
     bottomW/2, -height/2, -bottomD/2,
     bottomW/2, -height/2,  bottomD/2,
    -bottomW/2, -height/2,  bottomD/2,
    
    // Top face (y = height/2)
    -topW/2, height/2, -topD/2,
     topW/2, height/2, -topD/2,
     topW/2, height/2,  topD/2,
    -topW/2, height/2,  topD/2
  ];

  const indices = [
    // Bottom face
    0, 1, 2, 0, 2, 3,
    // Top face
    4, 6, 5, 4, 7, 6,
    // Side faces
    0, 4, 5, 0, 5, 1, // Front
    1, 5, 6, 1, 6, 2, // Right
    2, 6, 7, 2, 7, 3, // Back
    3, 7, 4, 3, 4, 0  // Left
  ];

  geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  
  return geometry;
}

// Decorative GLB pebble accent. Loaded on demand from createEnvironment so the
// scene already exists by the time the async load resolves.
function addPebble() {
  loader.load(
    'assets/models/Pebble-Square.glb',
    function(gltf) {
      const model = gltf.scene;
      model.position.set(0, -30, 0);
      model.scale.setScalar(148.5);
      model.rotation.y = Math.PI * 0.53;
      model.traverse(function(child) {
        if (child.isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
      app.scene.add(model);
    },
  );
}

// Settings-drawer toggle: darken the zen-garden gravel. The gravel plane's
// material color multiplies its texture map, so we stash the original color once
// and swap to a darker gray, restoring the original when turned off.
function setGravelDark(dark) {
  gravelDark = dark;
  if (!gravel) return;
  const mat = gravel.material;
  if (!mat.userData.originalColor) mat.userData.originalColor = mat.color.clone();
  if (dark) mat.color.setHex(0x494b4d);
  else mat.color.copy(mat.userData.originalColor);
}

    function addZenGardenRocks() {
  const zenGroup = new THREE.Group();

  // Two stone materials so the standing stones don't all wear the same normal —
  // the cliff and fracture grains each have distinctive features that read as
  // obviously repeated when every rock shares one. Mixed across the placements below.
  const rockMaterialCliff = new THREE.MeshStandardMaterial({
    color: 0x5f5f5f,
    roughness: 0.9,
    flatShading: true,
  });
  const rockMaterialFracture = rockMaterialCliff.clone();
  registerRock(rockMaterialCliff, 'rock-cliff-normal.jpg');
  registerRock(rockMaterialFracture, 'rock-fracture-normal.jpg');

  // Helper to sculpt an organic but solid rock
  function makeZenRock(size = 12, stretch = [1, 1, 1], material = rockMaterialCliff) {
    const geo = new THREE.IcosahedronGeometry(size, 1); // low poly, but smooth
    const pos = geo.attributes.position;

    // Slightly shift vertices for a natural look
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const y = pos.getY(i);
      const z = pos.getZ(i);

      // Gentle, not noisy: push vertices outward a little
      const scale = 1 + (Math.sin(x * 0.5 + z) * 0.08 + Math.cos(y * 0.3) * 0.05);
      pos.setXYZ(i, x * scale * stretch[0], y * scale * stretch[1], z * scale * stretch[2]);
    }
    geo.computeVertexNormals();
    return new THREE.Mesh(geo, material);
  }

  // A few deliberate, "epic" stones. `mat` mixes the two normals so the reuse isn't
  // obvious — interleaved so no two adjacent stones share a grain.
  const placements = [
    { x: -270, z: -180, y: -30, size: 14, stretch: [1.6, 0.8, 1.1], mat: rockMaterialCliff },
    { x: -290, z: -140, y: -10, size: 12, stretch: [1, 2.4, 1], mat: rockMaterialFracture },
    { x: -240, z: 80, y: -5, size: 16, stretch: [1.2, 1, 0.9], mat: rockMaterialCliff },
    { x: 290, z: 150, y: -45, size: 22, stretch: [1, 1.4, 1], mat: rockMaterialFracture },
    { x: 240, z: 180, y: -15, size: 16, stretch: [1.8, 1, 0.9], mat: rockMaterialCliff },
  ];

  placements.forEach(p => {
    const rock = makeZenRock(p.size, p.stretch, p.mat);
    rock.position.set(p.x, -25, p.z);
    rock.rotation.y = Math.random() * Math.PI;
    rock.castShadow = true;
    rock.receiveShadow = true;
    zenGroup.add(rock);
  });

  app.scene.add(zenGroup);
  zenRocksGroup = zenGroup; // kept so winter mode can frost the rock tops
}

function addBackgroundRocks() {
  const rockGroup = new THREE.Group();

  for (let i = 0; i < 12; i++) {
    const rockSize = Math.random() * 4 + 2;
    const rockType = Math.floor(Math.random() * 3);
    let rockGeometry;

    switch (rockType) {
      case 0:
        rockGeometry = new THREE.TetrahedronGeometry(rockSize);
        break;
      case 1:
        rockGeometry = new THREE.OctahedronGeometry(rockSize);
        break;
      case 2:
        rockGeometry = new THREE.IcosahedronGeometry(rockSize, 0);
        break;
    }

    const rockMaterial = new THREE.MeshStandardMaterial({
      color: 0x808080,
      roughness: 0.9,
      flatShading: true,
    });

    const rock = new THREE.Mesh(rockGeometry, rockMaterial);
    rock.castShadow = true;
    rock.receiveShadow = true;

    // Scatter them around the ground
    rock.position.set(
      (Math.random() - 0.5) * 800,
      -28, // sit on ground
      (Math.random() - 0.5) * 800
    );

    // Flattened proportions
    rock.scale.y = Math.random() * 0.4 + 0.3;

    // Random rotation
    rock.rotation.set(
      Math.random() * Math.PI,
      Math.random() * Math.PI,
      Math.random() * Math.PI
    );

    rockGroup.add(rock);
  }

  app.scene.add(rockGroup);
}

// Snow caps for Bare / Winter mode. Rather than hand-place snow on each feature,
// fire short rays straight DOWN over the pot and rock footprints and drop a small
// white clump wherever a ray lands on an upward-facing surface — so snow settles
// on the rims, the soil and the rock tops automatically, whatever their shape. All
// clumps share one InstancedMesh (a single draw). Built once, then just toggled.
function buildSceneSnow() {
  const group = new THREE.Group();

  // Frost the pot (rim + soil) and the standing garden rocks.
  const targets = [];
  if (pot) pot.traverse(o => { if (o.isMesh) targets.push(o); });
  if (zenRocksGroup) zenRocksGroup.traverse(o => { if (o.isMesh) targets.push(o); });
  if (!targets.length) return group;

  const ray = new THREE.Raycaster();
  const DOWN = new THREE.Vector3(0, -1, 0);
  const Y_AXIS = new THREE.Vector3(0, 1, 0);
  const dummy = new THREE.Object3D();
  const placements = [];

  // Scatter sample rays over each target's footprint, denser for bigger tops.
  targets.forEach(mesh => {
    mesh.updateWorldMatrix(true, false);
    const box = new THREE.Box3().setFromObject(mesh);
    const w = box.max.x - box.min.x, d = box.max.z - box.min.z;
    const samples = Math.min(Math.max(Math.floor(w * d * 0.13), 10), 420);
    for (let s = 0; s < samples; s++) {
      const x = box.min.x + Math.random() * w;
      const z = box.min.z + Math.random() * d;
      ray.set(new THREE.Vector3(x, box.max.y + 30, z), DOWN);
      const hit = ray.intersectObject(mesh, false)[0];
      if (!hit || !hit.face) continue;
      const normal = hit.face.normal.clone().transformDirection(hit.object.matrixWorld);
      if (normal.y < 0.4) continue; // only upward-facing surfaces hold snow
      placements.push({ point: hit.point, normal });
    }
  });
  if (!placements.length) return group;

  const capGeo = new THREE.IcosahedronGeometry(1, 2); // smooth rounded clump
  const capMat = new THREE.MeshStandardMaterial({
    color: 0xeef4ff, roughness: 0.95, metalness: 0.0, flatShading: false,
    emissive: 0x2a3f5f, emissiveIntensity: 0.15, // faint blue frost, matching the tree snow
  });
  // Share the Surface Textures toggle: applies snow-normal.jpg now if textures are
  // already on, and setRockTexture flips it with the rock normals from then on.
  // sceneSnow is built (and registered) only when winter starts, so the map is
  // present exactly when both textures are on and snow is showing.
  registerRock(capMat, 'snow-normal.jpg');
  const count = placements.length;
  const caps = new THREE.InstancedMesh(capGeo, capMat, count);
  caps.castShadow = true;
  caps.raycast = () => {};

  // Rest transforms + seat normals kept so winter's end can melt the caps (shrink,
  // flatten, and sink into their surface) rather than blink them off — mirrors the
  // per-branch tree-snow thaw in tree.js. See meltSceneSnow().
  const basePos = new Float32Array(count * 3);
  const baseQuat = new Float32Array(count * 4);
  const baseScale = new Float32Array(count * 3);
  const normals = new Float32Array(count * 3);

  placements.forEach((p, i) => {
    const r = 2.3 + Math.random() * 1.6;
    dummy.position.copy(p.point).addScaledVector(p.normal, r * 0.25); // sit on top, not sunk
    dummy.quaternion.setFromUnitVectors(Y_AXIS, p.normal);
    dummy.rotateY(Math.random() * Math.PI * 2);
    // Wide and thick so neighbouring caps overlap into a continuous blanket.
    dummy.scale.set(r, r * 0.65, r);
    dummy.updateMatrix();
    caps.setMatrixAt(i, dummy.matrix);

    basePos[i * 3] = dummy.position.x; basePos[i * 3 + 1] = dummy.position.y; basePos[i * 3 + 2] = dummy.position.z;
    baseQuat[i * 4] = dummy.quaternion.x; baseQuat[i * 4 + 1] = dummy.quaternion.y;
    baseQuat[i * 4 + 2] = dummy.quaternion.z; baseQuat[i * 4 + 3] = dummy.quaternion.w;
    baseScale[i * 3] = r; baseScale[i * 3 + 1] = r * 0.65; baseScale[i * 3 + 2] = r;
    normals[i * 3] = p.normal.x; normals[i * 3 + 1] = p.normal.y; normals[i * 3 + 2] = p.normal.z;
  });
  caps.instanceMatrix.needsUpdate = true;
  group.userData.melt = { caps, count, basePos, baseQuat, baseScale, normals };

  group.add(caps);
  app.scene.add(group);
  return group;
}

// Seconds for the garden frost to thaw out / settle in as winter ends / begins —
// matches the tree-snow melt+settle (tree.js) so the pot/rock caps and the painted
// ground/peak snow recede or accumulate together.
const SCENE_MELT_S = 3.0;
let _snowMeltTween = null;          // active thaw/accumulate, killed if winter re-toggles mid-tween
const _sceneSnowState = { m: 1 };   // 0 = full winter coat, 1 = fully thawed/bare (persists across toggles)
const _gravelSnow = new THREE.Color(0xe6ebf2);
const _gravelTarget = new THREE.Color();
const _meltPos = new THREE.Vector3();
const _meltQuat = new THREE.Quaternion();
const _meltScl = new THREE.Vector3();
const _meltNrm = new THREE.Vector3();
const _meltMat = new THREE.Matrix4();

// Drive every winter-frost surface to thaw progress m (0 = full winter, 1 = bare):
// fade the painted ground/peak/forest snow, lerp the gravel back to its summer tint,
// and shrink/flatten/sink the pot+rock caps. m=0 restores the full coat (used to
// snap back if winter re-enables mid-thaw).
function applySceneMelt(m) {
  const w = 1 - m;
  if (groundShader) groundShader.uniforms.uWinter.value = w;
  for (const mesh of [app.backdrops.mountains, app.backdrops.fuji, app.backdrops.forest]) {
    if (mesh && mesh.material.userData.shader) mesh.material.userData.shader.uniforms.uWinter.value = w;
  }
  if (gravel) gravel.material.color.lerpColors(_gravelTarget, _gravelSnow, w);

  const md = sceneSnow && sceneSnow.userData.melt;
  if (md) {
    const shrink = 1 - m;
    const flat = (1 - m) * (1 - m); // height collapses faster → caps puddle before they go
    const sink = m * m;             // accelerating slide into the surface
    for (let i = 0; i < md.count; i++) {
      const sx = md.baseScale[i * 3], sy = md.baseScale[i * 3 + 1], sz = md.baseScale[i * 3 + 2];
      _meltScl.set(sx * shrink, sy * flat, sz * shrink);
      _meltQuat.set(md.baseQuat[i * 4], md.baseQuat[i * 4 + 1], md.baseQuat[i * 4 + 2], md.baseQuat[i * 4 + 3]);
      _meltNrm.set(md.normals[i * 3], md.normals[i * 3 + 1], md.normals[i * 3 + 2]);
      _meltPos.set(md.basePos[i * 3], md.basePos[i * 3 + 1], md.basePos[i * 3 + 2])
        .addScaledVector(_meltNrm, -sink * sy); // sink down into the surface
      _meltMat.compose(_meltPos, _meltQuat, _meltScl);
      md.caps.setMatrixAt(i, _meltMat);
    }
    md.caps.instanceMatrix.needsUpdate = true;
  }
}

// Toggle the static-garden frost on/off in step with Bare / Winter mode. Driven by
// snow.js's updateWinter(); idempotent so it can be called freely. Enabling settles
// the frost in (caps grow, snow paints on, gravel whitens) and disabling thaws it
// out, both over SCENE_MELT_S and both interruptible (the tween eases from wherever
// the coat currently is). Pass animate=false to snap to the target instantly — used
// for the boot/load sync so an already-wintry tree shows its full coat at once. See
// applySceneMelt.
function setEnvironmentSnow(enabled, animate = true) {
  if (enabled === _snowOn) return;
  _snowOn = enabled;

  if (gravel && !gravel.material.userData.originalColor) {
    gravel.material.userData.originalColor = gravel.material.color.clone();
  }
  // Summer (non-winter) gravel tint the lerp resolves to (Darker Gravel–aware).
  _gravelTarget.copy(gravelDark ? new THREE.Color(0x494b4d)
    : (gravel ? gravel.material.userData.originalColor : _gravelSnow));

  if (enabled && !sceneSnow) sceneSnow = buildSceneSnow();
  if (sceneSnow) sceneSnow.visible = true; // visible through the whole tween; hidden only after a full thaw

  if (_snowMeltTween) { _snowMeltTween.kill(); _snowMeltTween = null; }
  const target = enabled ? 0 : 1; // 0 = full winter coat, 1 = bare; ease from the current m

  if (!animate) {
    _sceneSnowState.m = target;
    applySceneMelt(target);
    if (!enabled && sceneSnow) sceneSnow.visible = false;
    return;
  }

  _snowMeltTween = gsap.to(_sceneSnowState, {
    m: target, duration: SCENE_MELT_S, ease: enabled ? 'power2.out' : 'power1.in',
    onUpdate: () => applySceneMelt(_sceneSnowState.m),
    onComplete: () => {
      _snowMeltTween = null;
      if (!enabled && sceneSnow) sceneSnow.visible = false; // fully bare → hide the caps
    }
  });
  applySceneMelt(_sceneSnowState.m); // apply the starting frame now so nothing snaps
}

// Dry the distant grass to brown when the foliage palette is Autumn. Driven each
// frame from animate() with parameters.foliageColor === 'autumn'; idempotent so
// the per-frame call is a cheap no-op outside the transition. (The grass disc may
// be unbuilt/uncompiled, in which case its uniform inits from _autumnOn instead.)
function setEnvironmentAutumn(enabled) {
  if (enabled === _autumnOn) return;
  _autumnOn = enabled;
  const a = enabled ? 1 : 0;
  if (groundShader) groundShader.uniforms.uAutumn.value = a;
  const forest = app.backdrops.forest;
  if (forest && forest.material.userData.shader) forest.material.userData.shader.uniforms.uAutumn.value = a;
}

export { createEnvironment, setMountainsVisible, setFujiVisible, setForestVisible, setPagodaVisible, setCourtyardVisible, setHouseVisible, setLanternVisible, setToriiVisible, setShortLanternVisible, setWaterCanVisible, setStoolVisible, setTeaVisible, setBambooVisible, setGravelDark, setEnvironmentSnow, setEnvironmentAutumn, setLanternNight };
